import {
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    Chip,
    useTheme
} from "@mui/material";
import Box from "@mui/material/Box";
import { IconCheck, IconX } from "@tabler/icons-react";
import React, { useContext, useCallback } from 'react';
import { Context as AppContext } from "../../context/AppContext";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { toast } from "react-toastify";
import './mentorHomepage.css'

const ListOfInterns = ({ students, applications = true }) => {
    const theme = useTheme();
    const {
        state: { currentUser },
        updateUserData,
        updateInternData,
        createInternship,
        getApplicants,
        sendMatchedEmails
    } = useContext(AppContext);

    const handleAccept = useCallback(async (studentId) => {
        try {
            const updatedUser = {
                ...currentUser,
                mentees: currentUser.mentees ? [...currentUser.mentees, studentId] : [studentId]
            };
            const internshipUid = crypto.randomUUID();

            await updateUserData(updatedUser);
            await updateInternData({
                mentorUid: currentUser.uid,
                uid: studentId,
                acceptedAt: Date.now(),
                internshipUid: internshipUid,
            });
            await createInternship({
                mentorUid: currentUser.uid,
                uid: internshipUid,
                studentUid: studentId,
                startDate: new Date().toISOString(),
                endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
            });

            toast.success('Student accepted successfully!');

            // Find the accepted student's data
            const acceptedStudent = students.find(student => student.uid === studentId);

            
            // Send matched emails to both mentor and mentee
            try {
                await sendMatchedEmails(
                    currentUser.email || '',
                    currentUser.name || '',
                    acceptedStudent?.email || '',
                    acceptedStudent?.name || '',
                    acceptedStudent?.school || 'AIDEN Industry Horizons'
                );
                console.log('Matched emails sent successfully');
            } catch (emailError) {
                console.error('Error sending matched emails:', emailError);
                toast.warning('Match created successfully, but failed to send notification emails');
            }

            await rejectAllOtherApplicants(studentId);

        } catch (error) {
            console.error('Error accepting student:', error);
            toast.error('Failed to accept student. Please try again.');
        }
    }, [currentUser, updateUserData, updateInternData, createInternship, sendMatchedEmails, students]);

    const rejectAllOtherApplicants = async (acceptedStudentId) => {
        try {
            // Get all current applicants for this mentor
            const applicantsQuery = query(
                collection(db, "users"),
                where("role", "==", "INTERN"),
                where("mentorApplications", "array-contains", currentUser.uid)
            );
            
            const applicantsSnapshot = await getDocs(applicantsQuery);
            
            // Process each applicant except the accepted one
            for (const applicantDoc of applicantsSnapshot.docs) {
                const applicantData = applicantDoc.data();
                
                // Skip the student that was just accepted
                if (applicantData.uid === acceptedStudentId) {
                    continue;
                }
                
                // Remove this mentor's UID from their applications array
                const currentApplications = applicantData.mentorApplications || [];
                const updatedApplications = currentApplications.filter(app => app !== currentUser.uid);
                
                // Update the student's data
                await updateInternData({
                    uid: applicantData.uid,
                    mentorApplications: updatedApplications,
                    hasApplied: false
                });
                
            }

            toast.success("All other applicants have been automatically rejected");
            getApplicants(currentUser.uid);

        } catch (error) {
            console.error("Error rejecting other applicants:", error);
            toast.error("Failed to reject other applicants");
        }
    };


    const handleReject = useCallback(async (studentId) => {
        try {
            const studentRef = doc(db, "users", studentId);
            const studentDoc = await getDoc(studentRef);

            if (studentDoc.exists()) {
                const studentData = studentDoc.data();
                const currentApplications = studentData.mentorApplications || [];
                const updatedApplications = currentApplications.filter(app => app !== currentUser.uid);

                await updateInternData({
                    uid: studentId,
                    mentorApplications: updatedApplications,
                    hasApplied: false
                });

                toast.success("Student application rejected successfully");
                getApplicants(currentUser.uid);
            } else {
                toast.error("Student not found");
            }
        } catch (error) {
            console.error("Error rejecting student:", error);
            toast.error("Failed to reject student application");
        }
    }, [currentUser.uid, updateInternData, getApplicants]);

    if (!students) {
        return <Typography>Thank you for mentoring! You will be notified when you're matched with a
            student...</Typography>
    }

    return (
        <Box sx={{
            overflow: 'hidden',
            '& .MuiTable-root': {
                minWidth: '100%'
            }
        }}>
            <Table sx={{
                '& .MuiTableHead-root': {
                    '& .MuiTableCell-head': {
                        bgcolor: '#f8fafc',
                        color: '#374151',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2,
                        borderBottom: '2px solid #e5e7eb'
                    }
                },
                '& .MuiTableBody-root': {
                    '& .MuiTableRow-root': {
                        '&:hover': {
                            bgcolor: '#f9fafb'
                        },
                        '& .MuiTableCell-root': {
                            py: 2.5,
                            borderBottom: '1px solid #f1f5f9',
                            fontSize: '0.875rem'
                        }
                    }
                }
            }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 140 }}>Actions</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell sx={{ width: 80 }}>Age</TableCell>
                        <TableCell>Job Description</TableCell>
                        <TableCell>Interests</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {students?.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={5}
                                sx={{
                                    textAlign: 'center',
                                    py: 6,
                                    color: '#6b7280',
                                    fontStyle: 'italic'
                                }}
                            >
                                No students have applied for mentorship yet...
                            </TableCell>
                        </TableRow>
                    )}
                    {students.map(item => {
                        const isMentee = currentUser.mentees?.includes(item.uid);

                        return (
                            <TableRow key={item?.uid}>
                                <TableCell>
                                    {isMentee ? (
                                        <Chip
                                            label="MENTEE"
                                            size="small"
                                            className="rainbow-text"
                                            sx={{
                                                bgcolor: theme.palette.secondary.main,
                                                color: theme.palette.accent.main,
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                height: 28
                                            }}
                                        />
                                    ) : (
                                        <Stack direction="row" gap={1}>
                                            <Tooltip title="Accept Application">
                                                <IconButton
                                                    onClick={() => handleAccept(item.uid)}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: theme.palette.secondary.main,
                                                        color: theme.palette.accent.main,
                                                        '&:hover': {
                                                            bgcolor: theme.palette.accent.main + '20',
                                                            transform: 'scale(1.05)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <IconCheck size={18} stroke={2} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reject Application">
                                                <IconButton
                                                    onClick={() => handleReject(item.uid)}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: '#fef2f2',
                                                        color: '#dc2626',
                                                        '&:hover': {
                                                            bgcolor: '#fecaca',
                                                            transform: 'scale(1.05)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <IconX size={18} stroke={2} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500, color: '#374151' }}>                                    {item?.name}
                                </TableCell>
                                <TableCell sx={{ color: '#6b7280' }}>
                                    {item?.age}
                                </TableCell>
                                <TableCell sx={{
                                    color: '#374151',
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {item.jobDescription}
                                </TableCell>
                                <TableCell sx={{
                                    color: '#6b7280',
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {item.interests}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </Box>
    );
};

export default ListOfInterns;
